import { Injectable } from 'angular-ts-decorators';

import { SpynlBase } from '@shared/app/scripts/services/spynl-base';
import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';

import {
  PackingListModel,
  PackingListStatus,
} from '@core/app/scripts/classes/packing-list-model';

/**
 * Factory in the coreModule. Related to {@link
 * coreModule.coreTransaction} - it communicates with the same
 * collection, but assuming different class for entities. Methods are inherited
 * form {@link coreModule.spynlBase spynlBase} service, and are documented
 * there.
 */
@Injectable('packingList')
export class PackingList extends SpynlBase<PackingListModel> {
  constructor(
    $http: ng.IHttpService,
    $q: ng.IQService,
    sentry,
    domain: Domain,
    config: IConfig,
    standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    super(
      $http,
      $q,
      sentry,
      domain,
      config,
      standardErrorParser,
      PackingListModel,
    );

    this.location = '/packing-lists';
  }

  /**
   * Downloads requested PackingList as pdf.
   * @param {string} _id PackingList id
   * @returns Promise that resolves wit hPdf file as string
   */
  download(_id: string) {
    return this.$http
      .post(
        `${this.domain.getSpynlService()}/packing-lists/download`,
        { _id },
        {
          responseType: 'arraybuffer',
          withCredentials: true,
        },
      )
      .then((response) => <string>response.data);
  }

  /**
   * Downloads requested PackingList as csv.
   * @param {string} _id PackingList id
   * @returns Promise which resolves with CSV file as string
   */
  downloadCsv(_id: string) {
    return this.$http
      .post(
        `${this.domain.getSpynlService()}/packing-lists/download-csv`,
        { _id },
        {
          responseType: 'arraybuffer',
          withCredentials: true,
        },
      )
      .then((response) => <string>response.data);
  }

  /**
   * Downloads requested PackingList as excel.
   * @param {string} _id PackingList id
   * @returns Promise which resolves with Excel file as string
   */
  downloadExcel(_id: string) {
    return this.$http
      .post(
        `${this.domain.getSpynlService()}/packing-lists/download-excel`,
        { _id },
        {
          responseType: 'arraybuffer',
          withCredentials: true,
        },
      )
      .then((response) => <string>response.data);
  }

  /**
   * Gets packing-lists metadata
   * @returns {Promise<any>} Promise that resolves with
   * packing-lists properties to be filtered by
   */
  // TODO improve type of return data
  getFilter(): ng.IPromise<{ [key: string]: string[] }> {
    return this.$http
      .post(
        // probably should go through services
        `${this.domain.getSpynl()}/packing-lists/filter`,
        undefined,
      )
      .then((response: any) => {
        return response.data.data;
      });
  }

  /**
   * Query the ship endpoint
   * @param {string} _id packing list id
   * @param {boolean} skipSendcloud true, if we want to mark packing list as
   * shipped without using send cloud
   * @param {boolean} printDocuments true, if we want automatically push the
   * packing list pdf and shipping label pdf into the printq.
   * @returns {Promise<any>} promise that resolves with _id of the added item
   */
  ship(
    _id: string,
    skipSendcloud?: boolean,
    printDocuments?: boolean,
  ): ng.IPromise<string> {
    return this.$http
      .post(
        // probably should go through services
        `${this.domain.getSpynl()}/packing-lists/ship`,
        { filter: { _id }, skipSendcloud, printDocuments },
      )
      .then((response: any) => {
        return response.data.data[0];
      });
  }

  /**
   * Get the pdf with labels
   * @param {string} _id packing list id
   * @returns {Promise<any>} promise that resolves with _id of the added item
   */
  getShippingLabels(_id: string): ng.IPromise<string> {
    return this.$http
      .post(
        // probably should go through services
        `${this.domain.getSpynl()}/packing-lists/shipping-labels`,
        { filter: { _id } },
        {
          responseType: 'arraybuffer',
          withCredentials: true,
        },
      )
      .then((response) => <string>response.data);
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be cancelled
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of cancelation
   */
  cancel(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/cancel', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be start qa
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of starting qa
   */
  startQa(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/start-qa', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be start picking.
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of starting picking.
   */
  startPicking(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/start-picking', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to complete from qa
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of starting qa
   */
  completeQa(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/complete-qa', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be reopen
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of setting to open
   */
  continue(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/continue', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to set to wait
   * @returns {ng.IPromise<any>}
   * Promise that resolves with confirmation of waiting
   */
  wait(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/wait', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be split
   * @returns {ng.IPromise<any>}
   * Promise that resolves with ids of splitted packingList
   */
  split(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/split', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be complete and discard unpicked
   * @returns {ng.IPromise<any>}
   * Promise that resolves with id of completed packingList
   */
  discardUnpicked(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/discard-unpicked', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be complete
   * @returns {ng.IPromise<any>}
   * Promise that resolves with id of completed packingList
   */
  complete(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/complete', {
        _id: packingList._id,
        status: 'complete',
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be stopped.
   * @returns {ng.IPromise<any>}
   * Promise that resolves with id of affected packingList
   */
  stopQa(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/stop-qa', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * @param {PackingListModel} packingList
   * The packing list to be stopped.
   * @returns {ng.IPromise<any>}
   * Promise that resolves with id of affected packingList
   */
  stopPicking(packingList: PackingListModel): ng.IPromise<any> {
    return this.$http
      .post(this.getLocation() + '/stop-picking', {
        _id: packingList._id,
      })
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }
}
